import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";

// Suppress a benign media rejection thrown by the QR scanner's internal <video>.play()
// when the element is removed mid-play (html5-qrcode). It is harmless and must not
// trigger the dev error-overlay which would block the /claim page.
if (typeof window !== "undefined") {
  const isBenignMedia = (msg) => {
    const s = String(msg || "");
    return (
      s.includes("play() request was interrupted") ||
      s.includes("The play() request") ||
      s.includes("media was removed from the document") ||
      s.includes("Cannot stop, scanner is not running")
    );
  };
  // Root-cause fix: monkey-patch HTMLMediaElement.play so that its returned
  // promise ALWAYS handles benign AbortError rejections at the source. This
  // prevents an unhandledrejection event from ever being dispatched, so the
  // webpack-dev-server / react-error-overlay iframe never appears.
  try {
    if (typeof HTMLMediaElement !== "undefined" && HTMLMediaElement.prototype.play) {
      const originalPlay = HTMLMediaElement.prototype.play;
      HTMLMediaElement.prototype.play = function patchedPlay() {
        const p = originalPlay.apply(this, arguments);
        if (p && typeof p.then === "function") {
          return p.catch((err) => {
            if (err && (err.name === "AbortError" || isBenignMedia(err.message))) {
              return; // swallow benign media rejection
            }
            throw err;
          });
        }
        return p;
      };
    }
  } catch (e) {
    if (process.env.NODE_ENV === "development") console.debug("media play patch skipped:", e?.message);
  }
  // Belt-and-suspenders: still swallow benign rejections/errors if any leak.
  window.addEventListener(
    "unhandledrejection",
    (e) => {
      const r = e.reason;
      const msg = r && (r.message || (typeof r.toString === "function" && r.toString()));
      if ((r && r.name === "AbortError") || isBenignMedia(msg)) {
        e.preventDefault();
        if (e.stopImmediatePropagation) e.stopImmediatePropagation();
      }
    },
    true
  );
  window.addEventListener(
    "error",
    (e) => {
      if (isBenignMedia(e && e.message)) {
        e.preventDefault();
        if (e.stopImmediatePropagation) e.stopImmediatePropagation();
      }
    },
    true
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);
